import { useState, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import { usePushToken } from '@/hooks/usePushToken'
import { useNotificationNavigation } from '@/hooks/useNotificationNavigation'

// Layouts
import MainLayout from '@/components/layouts/MainLayout'
import AuthLayout from '@/components/layouts/AuthLayout'
import { TrialLockGate } from '@/components/Gate'

// Auth
import Login from '@/pages/auth/Login'
import OTPVerify from '@/pages/auth/OTPVerify'
import EmailOTPVerify from '@/pages/auth/EmailOTPVerify'

// Pages
import ChildProfile from '@/pages/onboarding/ChildProfile'
import SituationPicker from '@/pages/onboarding/SituationPicker'
import BehaviorQuestionnaire from '@/pages/onboarding/BehaviorQuestionnaire'
import QuestionnaireResults from '@/pages/onboarding/QuestionnaireResults'
import Home from '@/pages/Home'
import Programs from '@/pages/Programs'
import ProgramDetail from '@/pages/ProgramDetail'
import Library from '@/pages/Library'
import MyChild from '@/pages/MyChild'
import Settings from '@/pages/Settings'
import SituationDetail from '@/pages/SituationDetail'
import ActivityDetail from '@/pages/ActivityDetail'
import PrintableDetail from '@/pages/PrintableDetail'
import ActivityComplete from '@/pages/ActivityComplete'
import Subscription from '@/pages/Subscription'
import DIYAll from '@/pages/DIYAll'
import DIYCategory from '@/pages/DIYCategory'
import AskMelloMap from '@/pages/AskMelloMap'
import BehaviorJournal from '@/pages/BehaviorJournal'
import EmotionCheckIn from '@/pages/EmotionCheckIn'
import Milestones from '@/pages/Milestones'
import SchoolReadiness from '@/pages/SchoolReadiness'
import RoutineBuilder from '@/pages/RoutineBuilder'
import FoodTracker from '@/pages/FoodTracker'
import SelfCare from '@/pages/SelfCare'
import Videos from '@/pages/Videos'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const setUser = useAuthStore((state) => state.setUser)
  const setTokens = useAuthStore((state) => state.setTokens)
  const [hydrated, setHydrated] = useState(useAuthStore.persist.hasHydrated())

  useEffect(() => {
    const unsubscribe = useAuthStore.persist.onFinishHydration(() => {
      setHydrated(true)
    })
    return unsubscribe
  }, [])

  // Wait for store to hydrate from storage before redirecting
  if (!hydrated) {
    return null
  }

  if (!isAuthenticated) {
    // Local Vite dev only: auto-login a mock user so we can iterate without
    // OTP. NEVER in a production build (vite build → import.meta.env.DEV is
    // false) — production must go through the real WhatsApp-OTP flow, or the
    // backend rejects the fake token (401) and the app is non-functional.
    // Set VITE_REAL_AUTH=true to exercise the real OTP login flow locally
    // (e.g. `VITE_REAL_AUTH=true npm run dev`) without a production build.
    if (import.meta.env.DEV && import.meta.env.VITE_REAL_AUTH !== 'true') {
      setTokens('dev-token', 'dev-refresh-token')
      setUser({
        id: 'dev-user',
        phone_number: '+919999999999',
        name: 'Dev User',
        onboarding_completed: true,
        situations_selected: ['wont_sit_still', 'picky_eater', 'meltdowns', 'sleep_issues'],
        push_enabled: false,
        child_profile: {
          id: 'dev-child',
          name: 'Aarav',
          date_of_birth: '2022-06-15',
          age_months: 46,
          sensory_profile: {},
        },
      })
      return null
    }
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function App() {
  // Flutter bridge hooks - register push token and handle notification taps
  usePushToken()
  useNotificationNavigation()

  return (
    <Routes>
      {/* Public auth routes (real WhatsApp-OTP login) */}
      <Route path="/welcome" element={<Navigate to="/login" replace />} />
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<Login />} />
        <Route path="/verify" element={<OTPVerify />} />
        <Route path="/verify-email" element={<EmailOTPVerify />} />
      </Route>

      {/* Onboarding (authenticated but not completed) */}
      <Route element={<AuthLayout />}>
        <Route path="/onboarding/child" element={<ChildProfile />} />
        <Route path="/onboarding/questionnaire" element={<BehaviorQuestionnaire />} />
        <Route path="/onboarding/results" element={<QuestionnaireResults />} />
        <Route path="/onboarding/situations" element={<SituationPicker />} />
        {/* Keep old route for backwards compat */}
        <Route path="/onboarding/sensory" element={<SituationPicker />} />
      </Route>

      {/* Protected routes */}
      <Route
        element={
          <ProtectedRoute>
            <TrialLockGate>
              <MainLayout />
            </TrialLockGate>
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Home />} />
        <Route path="/programs" element={<Programs />} />
        <Route path="/programs/:programId" element={<ProgramDetail />} />
        <Route path="/library" element={<Library />} />
        <Route path="/my-child" element={<MyChild />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/situation/:situationId" element={<SituationDetail />} />
        <Route path="/diy" element={<DIYAll />} />
        <Route path="/diy/:categoryId" element={<DIYCategory />} />
        <Route path="/activity/:activityId" element={<ActivityDetail />} />
        <Route path="/printable/:printableId" element={<PrintableDetail />} />
        <Route path="/activity/:activityId/complete" element={<ActivityComplete />} />
        <Route path="/subscription" element={<Subscription />} />
        <Route path="/ask" element={<AskMelloMap />} />
        <Route path="/journal" element={<BehaviorJournal />} />
        <Route path="/check-in" element={<EmotionCheckIn />} />
        <Route path="/milestones" element={<Milestones />} />
        <Route path="/school-readiness" element={<SchoolReadiness />} />
        <Route path="/routines" element={<RoutineBuilder />} />
        <Route path="/food-tracker" element={<FoodTracker />} />
        <Route path="/self-care" element={<SelfCare />} />
        <Route path="/videos" element={<Videos />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default App
